'use strict';

var _ = require('underscore');
var $ = require('jquery');
var AjaxItem = require('./AjaxItem');
var mediator = require('../../js/mediator');
var config = require('../../js/config');
var utilities = require('../../js/utilities');
import {parallelArrayEach} from '../../utilities/uiLoopHelpers';
import breakpoints from '../../js/breakpoints';

const HTTP_STATUS_NO_CONTENT = 204;
export const RESPONSIVE_MODE = {
  ALL: 'AjaxLoader:ALL',
  DESKTOP_ONLY: 'AjaxLoader:DESKTOP_ONLY',
  MOBILE_ONLY: 'AjaxLoader:MOBILE_ONLY'
};

export class AjaxLoader {

  constructor() {
    this._onAjaxFail = this._onAjaxFail;
    this._onAjaxSuccess = this._onAjaxSuccess.bind(this);
    this._createAjaxItem = this._createAjaxItem.bind(this);
  }

  _createAjaxItem(wrapper) {
    new AjaxItem(wrapper);
  }

  /**
   * Check if the response is valid.
   *
   * Sometimes the server may error or return a full page HTML markup.
   *
   * @param {string} response
   * @returns {boolean}
   * @private
   */
  _isResponseValid(response) {
    if (typeof response !== 'string') {
      return false;
    }
    return response.indexOf('<title>') === -1;
  }

  /**
   * When the ajax result has successfully returned
   *
   * @param {string} response
   * @param {string} url
   * @param {number} status
   * @private
   */
  _onAjaxSuccess(response, url, status) {
    if (status === HTTP_STATUS_NO_CONTENT || this._isResponseValid(response)) {
      mediator.trigger(config.events.ajax.success, { url, response });
    } else {
      mediator.trigger(config.events.ajax.failed, { url });
    }
  }

  /**
   * When the ajax result has errored.
   *
   * @param url
   * @private
   */
  _onAjaxFail(url) {
    mediator.trigger(config.events.ajax.failed, { url });
  }

  /**
   * Load the content from the url
   *
   * @param {string} url The url to request
   * @param {Object} post Any post data to send
   * @param responsiveMode One of the RESPONSIVE_MODE constants
   * @return {jQuery.Deferred}
   * @private
   */
  _load(url, post, responsiveMode = RESPONSIVE_MODE.ALL) {
    // Add the currency to the URL
    let currencyUrl = utilities.queryStringUrl(url, {
      cur: config.locale.currencyIsoCode
    });
    let promise;
    // For this implementation we are not catering for
    // listening for mobile->desktop responsive change
    if (this._checkResponsiveModeOk(responsiveMode)) {
      promise = $.ajax(currencyUrl, {
        method: post ? 'POST' : 'GET',
        cache: true,
        dataType: 'html',
        handleErrorLocally: true,
        originalUrl: url,
        data: utilities.addCustIpToDataObject(post)
      })
        .done((response, textStatus, xhr) => {
          this._onAjaxSuccess(response, url, xhr.status)
        })
        .fail(() => this._onAjaxFail(url));
    } else {
      promise = new Promise.resolve({});
    }
    return promise;
  }

  /**
   * Check whether the current responsive mode applies
   *
   * @param responsiveMode
   * @return {boolean}
   * @private
   */
  _checkResponsiveModeOk(responsiveMode) {
    switch (responsiveMode) {
      case RESPONSIVE_MODE.DESKTOP_ONLY:
        return breakpoints.isGreaterThan(config.breakpoint.tablet);
      case RESPONSIVE_MODE.MOBILE_ONLY:
        return breakpoints.isLessThan(config.breakpoint.laptop);
    }
    return true;
  }

  /**
   * Create wrappers properties from elements
   *
   * @param $wrappers
   * @private
   */
  _createItems($wrappers) {
    let wrappers = _.map($wrappers, el => {
      let $el = $(el),
        url = $el.data('endPoint'),
        post = $el.data('post'),
        replace = $el.data('replace'),
        responsiveMode = $el.is('.AjaxLoader--isDesktopOnly') ? RESPONSIVE_MODE.DESKTOP_ONLY : RESPONSIVE_MODE.ALL,
        containsRrPlacement = !!$el.data('containsRrPlacement'),
        hash = false,
        hashPos = url.indexOf('#');

      if (hashPos > -1) {
        hash = url.substr(hashPos);
        url = url.substr(0, hashPos);
      }

      return {
        el,
        url,
        hash,
        post,
        replace,
        responsiveMode,
        containsRrPlacement
      };
    });

    parallelArrayEach(wrappers, this._createAjaxItem, () => {
      let requests = this.sendLoadRequests(wrappers);
      this.monitorLoadRequests(requests);
    });

  }


  /**
   * @param wrappers An array of the wrappers which have been found
   * @param wrappers[].url The url for the endpoint
   * @param wrappers[].post An optional object of post data
   */
  filterUniqueValidRequests(wrappers) {
    return _.chain(wrappers)
      .filter((wrapper) => this._checkResponsiveModeOk(wrapper.responsiveMode))
      .unique((wrapper) => wrapper.url + JSON.stringify(wrapper.post))
      .value();
  }


  /**
   * @param wrappers An array of the wrappers which have been found
   * @param wrappers[].el The wrapper element
   * @param wrappers[].url The url for the endpoint
   * @param wrappers[].hash An id within the response to load
   * @param wrappers[].post An optional object of post data
   * @param wrappers[].replace Replace the AjaxLoader element with the results
   * @param wrappers[].responsiveMode One of the RESPONSIVE_MODE constants
   * @private
   */
  sendLoadRequests(wrappers) {
    const validWrappers = this.filterUniqueValidRequests(wrappers);
    return _.map(validWrappers, wrapper => {
      return {
        wrapper,
        promise: this._load(
          wrapper.url,
          wrapper.post,
          wrapper.responsiveMode
        )
      };
    });
  }

  /**
   * Monitor the requests that have been sent and trigger
   * events when they have all completed
   *
   * @param requests
   */
  monitorLoadRequests(requests) {
    // Trigger an event once all of the wrappers have finished loading
    let allRequests = _.map(requests, req => req.promise);
    $.when.apply($, allRequests).always(() => {
      mediator.trigger(config.events.ajax.wrappersComplete);
    });

    // Trigger an event once all of the wrappers that contain
    // rich relevance placements
    // have finished loading
    let richRelevanceRequests = _.chain(requests)
      .filter(req => req.wrapper.containsRrPlacement)
      .map(req => req.promise)
      .value();

    if (richRelevanceRequests.length) {
      $.when.apply($, richRelevanceRequests).always(() => {
        mediator.trigger(config.events.ajax.wrappersContainingRichRelevanceComplete);
      });
    }
  }

}

const ajaxLoader = new AjaxLoader();

export default ajaxLoader;

export function createAjaxLoader($parent) {

  mediator.on('responseApi:articlesVideos', function() {
    var $wrappers = $parent.find('.AjaxLoader');
    if ($wrappers.length) {
      ajaxLoader._createItems($wrappers);
    }
  }, this);

  var $wrappers = $parent.find('.AjaxLoader');
  if ($wrappers.length) {
    ajaxLoader._createItems($wrappers);
  }
}



// WEBPACK FOOTER //
// ./assets/components/AjaxLoader/AjaxLoader.js